import { useEffect, useMemo, useState } from "react";
import { Bell, ArrowRight, Repeat, Receipt, Calendar } from "lucide-react";
import { Link } from "react-router-dom";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import MoneyAmount from "@/components/MoneyAmount";

function bucket(daysAhead) {
  if (daysAhead <= 0) return "today";
  if (daysAhead === 1) return "tomorrow";
  return "upcoming";
}

function priorityFor(b) {
  if (b === "today") return { color: "#EF4444", label: "Today" };
  if (b === "tomorrow") return { color: "#D98E36", label: "Tomorrow" };
  return { color: "#16A34A", label: "Upcoming" };
}

function Row({ item, currency }) {
  const d = new Date(item.due_date);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const days = Math.round((d.getTime() - today.getTime()) / (24 * 60 * 60 * 1000));
  const b = bucket(days);
  const p = priorityFor(b);
  const Icon = item.is_subscription ? Repeat : Receipt;
  return (
    <div className="p-bill-row flex items-center justify-between gap-3 px-3 py-2.5">
      <div className="flex min-w-0 items-center gap-3">
        <span
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-foreground/80"
          style={{ background: "hsl(var(--muted) / 0.6)", border: "1px solid hsl(var(--border) / 0.7)" }}
        >
          <Icon className="h-4 w-4" strokeWidth={2} />
        </span>
        <div className="min-w-0">
          <div className="truncate text-[13px] font-semibold tracking-tight">{item.label}</div>
          <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <span
              aria-hidden
              className="inline-block h-2 w-2 rounded-full"
              style={{ background: p.color, boxShadow: `0 0 0 3px ${p.color}22` }}
            />
            <span>{days === 0 ? "Today" : days === 1 ? "Tomorrow" : `in ${days} days`}</span>
            <span aria-hidden>·</span>
            <span className="inline-flex items-center gap-1"><Calendar className="h-2.5 w-2.5" /> {d.toLocaleDateString()}</span>
          </div>
        </div>
      </div>
      <div className="shrink-0 text-right font-display text-[13.5px] font-bold tabular-nums">
        <MoneyAmount value={item.amount} currency={currency} tone="neutral" signed={item.kind === "income"} />
      </div>
    </div>
  );
}

function Section({ title, items, currency, color }) {
  if (!items.length) return null;
  return (
    <div className="mt-3 first:mt-0">
      <div className="px-3 pb-1.5 text-[10.5px] font-bold uppercase tracking-[0.18em]" style={{ color }}>
        {title} <span className="ml-1 rounded-full bg-muted/60 px-1.5 py-0.5 text-[10px] text-muted-foreground">{items.length}</span>
      </div>
      <div>{items.map((it) => <Row key={it.recurring_id + it.due_date} item={it} currency={currency} />)}</div>
    </div>
  );
}

export default function NotificationBell() {
  const { user } = useAuth();
  const currency = user?.currency || "USD";
  const [items, setItems] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchAll = () => {
      api.get("/reminders/upcoming?days_ahead=7")
        .then(({ data }) => { if (!cancelled) setItems(data.upcoming || []); })
        .catch(() => {});
      api.get("/notifications")
        .then(({ data }) => { if (!cancelled) setAlerts(data.items || []); })
        .catch(() => {});
    };
    fetchAll();
    const t = setInterval(fetchAll, 60 * 1000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const { today, tomorrow, upcoming, total } = useMemo(() => {
    const now = new Date(); now.setHours(0, 0, 0, 0);
    const today = [], tomorrow = [], upcoming = [];
    items.forEach((it) => {
      const days = Math.round((new Date(it.due_date).getTime() - now.getTime()) / (24 * 60 * 60 * 1000));
      const b = bucket(days);
      if (b === "today") today.push(it);
      else if (b === "tomorrow") tomorrow.push(it);
      else upcoming.push(it);
    });
    const unreadAlerts = alerts.filter((a) => !a.read).length;
    return { today, tomorrow, upcoming, total: today.length + tomorrow.length + upcoming.length + unreadAlerts };
  }, [items, alerts]);

  const badge = total > 99 ? "99+" : total;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="picon-btn picon-bell"
          aria-label={total ? `${total} reminders` : "No reminders"}
          data-testid="topbar-bell"
          title={total ? `${total} reminder${total === 1 ? "" : "s"}` : "No reminders"}
        >
          <Bell className="h-[18px] w-[18px]" strokeWidth={2} />
          {total > 0 && (
            <span
              aria-hidden
              className="absolute -top-1 -right-1 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[#EF4444] px-1 text-[10px] font-bold text-white shadow-[0_0_0_2px_hsl(var(--card)),0_4px_10px_-2px_rgba(239,68,68,0.55)]"
              data-testid="bell-count"
            >
              {badge}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={10}
        className="w-[360px] p-0"
        data-testid="bell-popover"
      >
        <div className="rounded-[14px] border border-border/70 bg-card shadow-[0_28px_56px_-22px_rgba(15,23,42,0.32)]">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3.5">
            <div>
              <div className="text-[10.5px] font-bold uppercase tracking-[0.2em] text-muted-foreground">Reminders</div>
              <div className="font-display text-[15px] font-extrabold tracking-tight">
                {total > 0 ? `${total} bill${total === 1 ? "" : "s"} due soon` : "All clear"}
              </div>
            </div>
            <div className="p-dot-live" aria-hidden />
          </div>
          <div className="pside-divider" />

          {/* Body */}
          <div className="max-h-[420px] overflow-y-auto px-1.5 py-2">
            {total === 0 ? (
              <div className="px-4 py-10 text-center">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl text-emerald-700 dark:text-emerald-300" style={{ background: "rgba(16,185,129,0.12)" }}>
                  <Bell className="h-5 w-5" />
                </div>
                <div className="mt-3 font-display text-sm font-bold">Nothing on the calendar</div>
                <div className="mt-1 text-[12px] text-muted-foreground">You{`'`}ll be notified when a bill is due in the next 7 days.</div>
              </div>
            ) : (
              <>
                {alerts.filter((a) => !a.read).length > 0 && (
                  <div className="mt-3 first:mt-0" data-testid="notif-alerts-section">
                    <div className="px-3 pb-1.5 text-[10.5px] font-bold uppercase tracking-[0.18em]" style={{ color: "#B91C1C" }}>
                      Alerts <span className="ml-1 rounded-full bg-muted/60 px-1.5 py-0.5 text-[10px] text-muted-foreground">{alerts.filter((a) => !a.read).length}</span>
                    </div>
                    <div>
                      {alerts.filter((a) => !a.read).map((a) => (
                        <a
                          key={a.id}
                          href={a.link || "/"}
                          onClick={(e) => {
                            e.preventDefault();
                            api.post(`/notifications/${a.id}/read`).catch(() => {});
                            setAlerts((prev) => prev.map((x) => x.id === a.id ? { ...x, read: true } : x));
                            setOpen(false);
                            if (a.link) window.location.href = a.link;
                          }}
                          className="block px-3 py-2 hover:bg-accent/40 rounded-md"
                          data-testid={`notif-alert-${a.id}`}
                        >
                          <div className="text-[13px] font-semibold truncate">{a.title}</div>
                          <div className="text-[12px] text-muted-foreground truncate">{a.body}</div>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
                <Section title="Due Today" items={today} currency={currency} color="#B91C1C" />
                <Section title="Due Tomorrow" items={tomorrow} currency={currency} color="#B5572E" />
                <Section title="Upcoming" items={upcoming} currency={currency} color="#047857" />
              </>
            )}
          </div>

          <div className="pside-divider" />
          {/* Footer CTA */}
          <Link
            to="/recurring"
            onClick={() => setOpen(false)}
            className="group flex items-center justify-between px-4 py-3 text-[13px] font-bold text-foreground transition-colors hover:bg-muted/50"
            data-testid="bell-view-all"
          >
            View all reminders
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}
